import { useState, useEffect } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

const ColorPickerTool = () => {
  const [color, setColor] = useState('#2563EB');
  const [rgb, setRgb] = useState({ r: 37, g: 99, b: 235 });
  const [hsl, setHsl] = useState({ h: 217, s: 91, l: 53 });

  useEffect(() => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    setRgb({ r, g, b });

    // Convert RGB to HSL
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rNorm:
          h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
          break;
        case gNorm:
          h = ((bNorm - rNorm) / d + 2) / 6;
          break;
        case bNorm:
          h = ((rNorm - gNorm) / d + 4) / 6;
          break;
        default:
          break;
      }
    }

    setHsl({
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    });
  }, [color]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const colorFormats = [
    { label: 'HEX', value: color.toUpperCase() },
    { label: 'RGB', value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
    { label: 'HSL', value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` },
  ];

  return (
    <div className="space-y-6" data-testid="color-picker-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Color Picker</h3>
      </div>

      {/* Color Preview */}
      <div className="flex gap-4 items-center">
        <div
          className="w-24 h-24 rounded-xl border border-border shadow-lg"
          style={{ backgroundColor: color }}
          data-testid="color-preview"
        />
        <div className="flex-1">
          <label className="text-sm text-muted-foreground mb-2 block">Pick a color</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-12 h-10 rounded cursor-pointer border-0 bg-transparent"
              data-testid="color-input"
            />
            <Input
              type="text"
              value={color}
              onChange={(e) => {
                const val = e.target.value;
                if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                  setColor(val);
                }
              }}
              className="flex-1 font-mono uppercase bg-background/50"
              placeholder="#000000"
              data-testid="hex-input"
            />
          </div>
        </div>
      </div>

      {/* Color Formats */}
      <div className="space-y-3">
        {colorFormats.map((format) => (
          <div
            key={format.label}
            className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border"
          >
            <div>
              <span className="text-xs text-muted-foreground">{format.label}</span>
              <p className="font-mono text-sm" data-testid={`${format.label.toLowerCase()}-value`}>{format.value}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(format.value)}
              data-testid={`copy-${format.label.toLowerCase()}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Quick Colors */}
      <div>
        <label className="text-sm text-muted-foreground mb-2 block">Quick Colors</label>
        <div className="flex flex-wrap gap-2">
          {['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'].map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className="w-8 h-8 rounded-lg border border-border hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              data-testid={`quick-color-${c.replace('#', '')}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColorPickerTool;
